// pages/RequestReset.tsx — "/forgot-password" (M6 Task 5, spec D2). Same form idiom as
// SignIn/SignUp (Card + Label + Input). Deliberately shows the SAME confirmation message
// whether or not the email exists — better-auth's own `POST /request-password-reset` already
// returns an identical `{status:true}` response either way (anti-enumeration baked into the
// server, see `lib/auth.ts`'s doc comment on `sendResetPassword`), so this screen's copy just
// has to not contradict that by branching on the result.
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { useState } from 'react';
import { Link } from 'react-router';
import { authClient } from '../lib/auth-client';
import { useT } from '../locales/context';

export function RequestReset() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    // Errors (network, validation) are swallowed into the same success state on purpose —
    // an attacker probing for valid emails must see identical behavior either way. A genuine
    // network failure just means the user tries again, same as any "check your email" flow.
    await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' }).catch(() => {});
    setIsSubmitting(false);
    setSubmitted(true);
  }

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t.auth.requestResetTitle}</CardTitle>
            <CardDescription>
              {submitted ? t.auth.requestResetSubtitleSent : t.auth.requestResetSubtitlePending}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <p className="text-sm text-muted-foreground">{t.auth.requestResetBodySent}</p>
            ) : (
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">{t.auth.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t.auth.sendingResetLink : t.auth.sendResetLinkCta}
                </Button>
              </form>
            )}
            <p className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/sign-in" className="underline">
                {t.auth.backToSignIn}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
